import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import DocumentCanvasCollaborative from './DocumentCanvasCollaborative';
import useStore from '../../hooks/useStore';
import useDebounce from '../../hooks/useDebounce';
import TagEditor from './components/TagEditor';
import {
  DocumentCanvasWrapper,
  DocumentHeader,
  DocumentTitleInput,
  DocumentMetadata,
  MetadataItem,
  TagsContainer
} from './DocumentCanvas.styled';

/**
 * Document Canvas Wrapper Component
 * Wraps DocumentCanvasCollaborative with auto-save and metadata management
 * Connects to global chatDocumentsStore for persistence across modules
 */
const DocumentCanvas = observer(({
  documentId, // Existing document ID to load
  initialContent = '', // Initial content for new documents
  initialTitle = 'Untitled Document',
  onClose,
  agentUuid,
  conversationId,
  vaultUuid,
  enableAICopilot = true,
  onAddChatMessage,
  className,
  borderRadius,
  headerBgColor,
  footerBgColor
}) => {
  const { chatDocumentsStore } = useStore();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState(initialTitle);
  const [docId, setDocId] = useState(documentId);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef(null);
  
  // Debounced content for auto-save (1 second delay)
  const debouncedContent = useDebounce(content, 1000);
  
  // Initialize document
  useEffect(() => {
    if (documentId) {
      // Load existing document from store
      const existingDoc = chatDocumentsStore.getDocument(documentId);
      if (existingDoc) {
        // Ensure content is always a string
        const contentString = typeof existingDoc.content === 'string' 
          ? existingDoc.content 
          : String(existingDoc.content || '');
        
        setContent(contentString);
        setTitle(existingDoc.title || 'Untitled Document');
        setDocId(documentId);
    }
    } else {
      // Create new document in store
      const contentString = typeof initialContent === 'string'
        ? initialContent
        : String(initialContent || '');
      
      const newDocId = chatDocumentsStore.addDocument({
        title: initialTitle,
        content: contentString,
        agentUuid,
        conversationId,
        module: 'HomePage' // Can be made dynamic if needed
      });
      setDocId(newDocId);
      setContent(contentString);
      setTitle(initialTitle);
    }
  }, [documentId, initialContent, initialTitle, agentUuid, conversationId, chatDocumentsStore]);
  
  // Auto-save content when debounced value changes
  useEffect(() => {
    if (!docId || !debouncedContent) return;
      
    // Don't save if this is the initial load
    if (debouncedContent === initialContent && !documentId) return;
    
    chatDocumentsStore.updateDocument(docId, {
      content: debouncedContent
    });
  }, [debouncedContent, docId, chatDocumentsStore, initialContent, documentId]);
  
  // Handle content change from editor
  const handleContentChange = useCallback((newContent) => {
    // DocumentCanvasCollaborative passes editorState object, extract text
    let contentString = '';
      
    if (typeof newContent === 'string') {
      contentString = newContent;
    } else if (newContent && typeof newContent === 'object') {
      // EditorState object - extract text
      if (typeof newContent.toText === 'function') {
        contentString = newContent.toText();
      } else if (typeof newContent.toMarkdown === 'function') {
        contentString = newContent.toMarkdown();
      } else if (newContent.content) {
        contentString = String(newContent.content);
      } else {
        contentString = String(newContent);
      }
    } else {
      contentString = String(newContent || '');
    }
    
    setContent(contentString);
  }, []);
  
  // Handle title change
  const handleTitleChange = useCallback((e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    
    if (docId) {
      chatDocumentsStore.updateDocument(docId, {
        title: newTitle
      });
    }
  }, [docId, chatDocumentsStore]);
            
  // Handle title edit mode
  const handleTitleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  }, []);
  
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    if (!title.trim()) {
      setTitle('Untitled Document');
      if (docId) {
        chatDocumentsStore.updateDocument(docId, {
          title: 'Untitled Document'
        });
      }
    }
  }, [title, docId, chatDocumentsStore]);
  
  // Handle close
  const handleClose = useCallback(() => {
    // Final save before closing
    if (docId && content) {
      const contentString = typeof content === 'string'
        ? content
        : String(content || '');
      
      chatDocumentsStore.updateDocument(docId, {
        content: contentString
      });
    }
    
    chatDocumentsStore.clearCurrentDocument();
    
    if (onClose) {
      onClose();
    }
  }, [docId, content, chatDocumentsStore, onClose]);

  // Handle tag changes
  const handleAddTag = useCallback((tag) => {
    if (docId) {
      chatDocumentsStore.addTag(docId, tag);
    }
  }, [docId, chatDocumentsStore]);

  const handleRemoveTag = useCallback((tag) => {
    if (docId) {
      chatDocumentsStore.removeTag(docId, tag);
    }
  }, [docId, chatDocumentsStore]);
  
  // Get current document data
  const document = docId ? chatDocumentsStore.getDocument(docId) : null;
  const tags = document?.tags || [];
  const wordCount = document?.wordCount || 0;
  const lastEdited = document?.lastEditedAt;
  
  // Format last edited time
  const formatLastEdited = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return new Date(date).toLocaleDateString();
  };

  return (
    <DocumentCanvasWrapper className={className}>
      <DocumentHeader $bgColor={headerBgColor}>
        {isEditingTitle ? (
          <DocumentTitleInput
            ref={titleInputRef}
            value={title}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleTitleBlur();
              }
            }}
          />
        ) : (
          <DocumentTitleInput
            as="div"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
          </DocumentTitleInput>
                )}
        
        <DocumentMetadata>
          <MetadataItem>{wordCount} words</MetadataItem>
          {lastEdited && (
            <MetadataItem>{formatLastEdited(lastEdited)}</MetadataItem>
          )}
        </DocumentMetadata>
        
        <TagsContainer>
          <TagEditor
            tags={tags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
          />
        </TagsContainer>
      </DocumentHeader>
      
      <DocumentCanvasCollaborative
        initialContent={content}
        documentTitle={title}
        onClose={handleClose}
        onChange={handleContentChange}
        borderRadius={borderRadius}
        headerBgColor="transparent" // Header handled by wrapper
        footerBgColor={footerBgColor}
        agentUuid={agentUuid}
        vaultUuid={vaultUuid}
        enableAICopilot={enableAICopilot}
        onAddChatMessage={onAddChatMessage}
        skipExitConfirmation={true}
      />
    </DocumentCanvasWrapper>
  );
});

export default DocumentCanvas;
